/**
 * Email Template Context
 *
 * Provides shared email template state across the app, backed by the API
 * service layer (mirrors InvoiceTemplateContext).
 *
 * In Live mode the templates are loaded from GET /api/email-templates so the
 * billing configuration selectors in project creation / template forms show the
 * same templates that are configured in Settings > Templates. In Demo/Tour mode
 * the hardcoded DEFAULT_EMAIL_TEMPLATES sample set is used instead.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { DEFAULT_EMAIL_TEMPLATES } from '@/components/billing/EmailTemplateEditor';
import type { EmailTemplate, EmailTemplateType } from '@/components/billing/EmailTemplateEditor';
import { fetchEmailTemplates } from '@/services/emailTemplateService';
import type { ApiEmailTemplate } from '@/services/emailTemplateService';
import { getDataMode } from '@/lib/data-service';
import { useCompanyChange } from '@/hooks/use-company-change';

interface EmailTemplateContextType {
  emailTemplates: EmailTemplate[];
  setEmailTemplates: React.Dispatch<React.SetStateAction<EmailTemplate[]>>;
  getEmailTemplatesByType: (type: EmailTemplate['type']) => EmailTemplate[];
  reloadTemplates: () => Promise<void>;
}

const EmailTemplateContext = createContext<EmailTemplateContextType | undefined>(undefined);

/** Maps an API email template into the frontend EmailTemplate shape. */
function mapApiToFrontend(api: ApiEmailTemplate): EmailTemplate {
  let parsedVars: { to?: string[]; cc?: string[]; bcc?: string[] } = {};
  if (api.variables) {
    try { parsedVars = JSON.parse(api.variables); } catch { /* ignore malformed */ }
  }
  return {
    id: String(api.id),
    name: api.name,
    type: (api.category || 'invoice') as EmailTemplateType,
    to: parsedVars.to ?? [],
    cc: parsedVars.cc ?? [],
    bcc: parsedVars.bcc ?? [],
    subject: api.subject,
    body: '',
    bodyHtml: api.body,
    isDefault: api.isDefault,
    createdAt: new Date(api.createdAt),
    updatedAt: new Date(api.updatedAt),
  };
}

export function EmailTemplateProvider({ children }: { children: ReactNode }) {
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>(
    getDataMode() === 'live' ? [] : DEFAULT_EMAIL_TEMPLATES,
  );

  const loadTemplates = useCallback(() => {
    // In Demo/Tour mode there is no backend data — use the sample templates.
    if (getDataMode() !== 'live') {
      setEmailTemplates(DEFAULT_EMAIL_TEMPLATES);
      return () => {};
    }
    let cancelled = false;
    fetchEmailTemplates()
      .then((apiTemplates) => {
        if (!cancelled) {
          setEmailTemplates(apiTemplates.map(mapApiToFrontend));
        }
      })
      .catch((err) => {
        console.error('Failed to load email templates from API:', err);
      });
    return () => { cancelled = true; };
  }, []);

  // Load templates on mount.
  useEffect(() => {
    return loadTemplates();
  }, [loadTemplates]);

  // Re-fetch templates when the active company changes.
  useCompanyChange(() => {
    loadTemplates();
  });

  const getEmailTemplatesByType = useCallback(
    (type: EmailTemplate['type']) => emailTemplates.filter((t) => t.type === type),
    [emailTemplates],
  );

  const reloadTemplates = useCallback(async () => {
    if (getDataMode() !== 'live') {
      setEmailTemplates(DEFAULT_EMAIL_TEMPLATES);
      return;
    }
    try {
      const apiTemplates = await fetchEmailTemplates();
      setEmailTemplates(apiTemplates.map(mapApiToFrontend));
    } catch (err) {
      console.error('Failed to load email templates from API:', err);
    }
  }, []);

  return (
    <EmailTemplateContext.Provider value={{ emailTemplates, setEmailTemplates, getEmailTemplatesByType, reloadTemplates }}>
      {children}
    </EmailTemplateContext.Provider>
  );
}

export function useEmailTemplates() {
  const context = useContext(EmailTemplateContext);
  if (context === undefined) {
    throw new Error('useEmailTemplates must be used within an EmailTemplateProvider');
  }
  return context;
}
